/** Send button that flips to Stop while streaming (ChatBox has no built-in stop). Stop = client abort + server-side cancel (no-op until #4141); send is blocked while reattaching. */

import { useCallback, useContext, useEffect, useRef } from "react"
import { IconButton, Tooltip } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { Send, Stop } from "@mui/icons-material"
import { ChatComposerSendButton } from "@mui/x-chat"
import { useChatActions, useChatStatus } from "@mui/x-chat-headless"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { ComposerControlsContext } from "./composer-chrome"

type ComposerMode = "send" | "stop" | "busy"

// Nudge the paper-plane right; its visual weight sits left of its box.
const SEND_ICON_SX = { transform: "translateX(1px)" } satisfies SxProps<Theme>

// Match the x-chat send button: brand-blue circle, contrast icon.
const STOP_BUTTON_SX = {
  bgcolor: "var(--app-primary-filled)",
  color: "var(--mui-palette-primary-contrastText)",
  "&:hover": { bgcolor: "var(--mui-palette-primary-dark)" },
} satisfies SxProps<Theme>

export function ComposerSendStopButton() {
  const { isStreaming } = useChatStatus()
  const { stopStreaming } = useChatActions()
  const { isReattaching, stopServerStream } = useContext(ComposerControlsContext)
  const analytics = useAnalytics()

  // Server-side cancel before the client abort (mirrors the drawer's order, #4141).
  // Reported here rather than off the turn's abort: a nav-away aborts the same controller, and only this
  // button means "the answer was not what I wanted".
  const handleStop = useCallback(() => {
    analytics.track(AI_TEAMMATE_EVENTS.TURN_STOPPED)
    stopServerStream()
    stopStreaming()
  }, [analytics, stopServerStream, stopStreaming])

  const mode: ComposerMode = isStreaming ? "stop" : isReattaching ? "busy" : "send"

  // Re-home focus across the swap so keyboard activation never drops to <body> when a button unmounts.
  const containerRef = useRef<HTMLSpanElement>(null)
  const modeRef = useRef<ComposerMode>(mode)
  useEffect(() => {
    if (modeRef.current === mode) return
    const container = containerRef.current
    const hadFocus = container != null && container.contains(document.activeElement)
    modeRef.current = mode
    if (!hadFocus || container == null) return
    const button = container.querySelector<HTMLButtonElement>("button")
    if (button != null && !button.disabled) {
      button.focus()
      return
    }
    // Send is disabled (empty composer) after a stop — keep focus in the composer, not on <body>.
    container.closest("form")?.querySelector<HTMLTextAreaElement>("textarea")?.focus()
  }, [mode])

  return (
    <span ref={containerRef}>
      {mode === "stop" && (
        <Tooltip title="Stop generating">
          <IconButton aria-label="Stop generating" onClick={handleStop} data-testid="chatbox-stop-button" sx={STOP_BUTTON_SX}>
            <Stop />
          </IconButton>
        </Tooltip>
      )}
      {mode === "busy" && (
        <Tooltip title="Reconnecting to your conversation…">
          {/* A disabled button can't host a tooltip trigger directly — wrap it. */}
          <span>
            <IconButton aria-label="Reconnecting to your conversation" color="primary" disabled data-testid="chatbox-reattach-busy">
              <Stop />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {/* x-chat ships no glyph — pass the paper-plane (also stays distinct from the disabled circle). */}
      {mode === "send" && <ChatComposerSendButton><Send fontSize="small" sx={SEND_ICON_SX} /></ChatComposerSendButton>}
    </span>
  )
}
